import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  ChevronDownIcon,
  WrenchScrewdriverIcon,
  CheckBadgeIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  BoltIcon,
  PlusIcon,
  TrashIcon,
  PlayIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../App';
import { pdf } from '@react-pdf/renderer';
import { decrypt } from '../../utils/crypto.js';
import MaintenanceReportPdf from './MaintenanceReportPdf';
import { OFFLINE_QUEUE_KEY, SignaturePad } from './FormularioMantenimiento';

export default function ClientesMantenimiento() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const backendUrl = BASE_URL.replace('/api/v1/', '');
  const company = sessionStorage.getItem('company') || 1;
  const userId = decrypt(sessionStorage.getItem('userId'));
  const userName = decrypt(sessionStorage.getItem('name'));

  const [clientes, setClientes] = useState([]);
  const [equiposByClient, setEquiposByClient] = useState({});
  const [loadingEquipos, setLoadingEquipos] = useState({});
  const [search, setSearch] = useState('');
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [selectedEquipoForMaintenance, setSelectedEquipoForMaintenance] = useState(null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);

  // Offline Queue State
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [syncingOffline, setSyncingOffline] = useState(false);

  // Delivery Modal State (for reports in "Pendiente de entrega")
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveringContext, setDeliveringContext] = useState(null); // { reportSummary, equipo, cliente }
  const [deliveryRecommendations, setDeliveryRecommendations] = useState([]);
  const [deliveryObservations, setDeliveryObservations] = useState('');
  const [deliveryStep, setDeliveryStep] = useState('delivery_tech'); // 'delivery_tech' | 'customer'
  const [savedSignatures, setSavedSignatures] = useState([]);
  const [isDrawingNewTech, setIsDrawingNewTech] = useState(false);
  const [deliveryTechSignature, setDeliveryTechSignature] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [submittingDelivery, setSubmittingDelivery] = useState(false);

  const loadOfflineQueue = () => {
    try {
      const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
      setOfflineQueue(raw ? JSON.parse(raw) : []);
    } catch (e) {
      setOfflineQueue([]);
    }
  };

  const syncOfflineReports = async () => {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    const queue = raw ? JSON.parse(raw) : [];
    if (!queue.length || !navigator.onLine) return;

    try {
      setSyncingOffline(true);
      const remaining = [];
      let syncedCount = 0;

      for (const item of queue) {
        try {
          if (item.method === 'PUT') {
            await axios.put(item.endpoint, item.payload);
          } else {
            await axios.post(item.endpoint || '/saveMaintenanceReport', item.payload);
          }
          syncedCount++;
        } catch (err) {
          console.error('Error sincronizando reporte offline:', err);
          remaining.push(item);
        }
      }

      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(remaining));
      setOfflineQueue(remaining);

      if (syncedCount > 0) {
        Swal.fire({
          icon: 'success',
          title: 'Reportes Offline Sincronizados',
          text: `Se sincronizaron ${syncedCount} reporte(s) pendientes con el servidor.`,
          timer: 2200,
          showConfirmButton: false,
        });
        if (expandedClientId) {
          const res = await axios.get(
            `/getProjects/${company}?tipo=equipo&customerId=${expandedClientId}`
          );
          setEquiposByClient((prev) => ({
            ...prev,
            [expandedClientId]: res.data.data || [],
          }));
        }
      }
    } finally {
      setSyncingOffline(false);
    }
  };

  useEffect(() => {
    loadOfflineQueue();
    const handleOnline = () => {
      loadOfflineQueue();
      syncOfflineReports();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    const base = axios.defaults.baseURL || BASE_URL || '';
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${base.endsWith('/') ? base : base + '/'}${cleanPath}`;
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    const base = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    const token = sessionStorage.getItem('Token') || '';
    return `${base}${cleanPath}?token=${encodeURIComponent(token)}`;
  };

  const fetchImageAsBase64 = async (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    try {
      const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('Token') || '' : '';
      const authUrl = token
        ? url.includes('?')
          ? `${url}&token=${encodeURIComponent(token)}`
          : `${url}?token=${encodeURIComponent(token)}`
        : url;
      const response = await axios.get(authUrl, { responseType: 'blob' });
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      console.error('Error fetching image for PDF:', url, error);
      return null;
    }
  };

  const handleDownloadReportPDF = async (reportId, equipo, cliente) => {
    if (!reportId) return;
    try {
      const res = await axios.get(`/getMaintenanceReport/${reportId}`);
      const report = res.data.data;

      const resGroup = await axios.get(`/getProjectQuestionGroups/${equipo.id}`);
      const group = resGroup.data.data;

      const techName = report.technicianData?.name || equipo.lastMaintenance?.technician || 'Técnico';
      const deliveryTechName =
        report.deliveryTechnicianData?.name ||
        (report.status === 'Finalizado' ? techName : 'Pendiente de entrega');
      const continuedTechName = report.continuedByTechnicianData?.name || '';

      const technicianSignatureBase64 = await fetchImageAsBase64(
        getFullUrl(report.technician_signature)
      );
      const deliverySignatureBase64 = await fetchImageAsBase64(
        getFullUrl(report.delivery_technician_signature)
      );
      const customerSignatureBase64 = await fetchImageAsBase64(
        getFullUrl(report.customer_signature)
      );

      const answersMap = {};
      for (const ans of report.answers || []) {
        const photosBase64 = [];
        for (const p of ans.photos || []) {
          const fullUrl = getFullUrl(p);
          const b64 = await fetchImageAsBase64(fullUrl);
          if (b64) photosBase64.push({ preview: b64, originalUrl: fullUrl, path: p });
          else if (fullUrl) photosBase64.push({ preview: fullUrl, originalUrl: fullUrl, path: p });
        }
        answersMap[ans.question_id] = {
          question_id: ans.question_id,
          optionIds: ans.selected_options || [],
          text: ans.answer_text,
          photos: photosBase64,
        };
      }

      const parentClient =
        cliente ||
        clientes.find((c) => c.id === equipo.customerId || c.id === equipo.customerData?.id) ||
        {};
      const principalEmail =
        parentClient.contacto_principal?.correo ||
        parentClient.principalContact?.correo ||
        parentClient.correo ||
        parentClient.email ||
        equipo.customerData?.contactos?.find((c) => String(c.contactoPrincipal) === '1' || c.contactoPrincipal === true)?.correo ||
        equipo.customerData?.contactos?.[0]?.correo ||
        equipo.customerData?.correo ||
        equipo.customerData?.email ||
        '';

      const enrichedEquipo = {
        ...equipo,
        cliente: parentClient,
        customerName:
          parentClient.nombre || equipo.customerName || equipo.customerData?.nombre,
        customerData: {
          ...(equipo.customerData || {}),
          ...parentClient,
          correo: principalEmail,
          email: principalEmail,
          direccion:
            parentClient.direccion ||
            equipo.customerData?.direccion ||
            equipo.location ||
            equipo.address,
        },
        customerEmail: principalEmail,
      };

      const equipName = equipo.nombre || equipo.elevatorTypeName || 'Equipo';
      const blob = await pdf(
        <MaintenanceReportPdf
          data={{
            ...report,
            id: report.id,
            answers: answersMap,
            technicianSignature: technicianSignatureBase64,
            deliveryTechnicianSignature: deliverySignatureBase64,
            customerSignature: customerSignatureBase64,
          }}
          equipo={enrichedEquipo}
          group={group}
          technicianName={techName}
          deliveryTechnicianName={deliveryTechName}
          continuedByTechnicianName={continuedTechName}
          customerName={report.customer_name || ''}
          customerEmail={principalEmail}
          maintenanceType={report.maintenance_type || 'Preventivo'}
          recommendations={report.recommendations || []}
          activities={report.activities || []}
          backendUrl={backendUrl}
        />
      ).toBlob();
      const rawCliente = parentClient.nombre || equipo.customerName || equipo.customerData?.nombre || report.customer_name || 'Cliente';
      const rawEquipo = equipName;
      const rawTipo = report.maintenance_type || 'Preventivo';
      const cleanCliente = String(rawCliente).trim().replace(/[/\\?%*:|"<>]/g, '_');
      const cleanEquipo = String(rawEquipo).trim().replace(/[/\\?%*:|"<>]/g, '_');
      const cleanTipo = String(rawTipo).trim().replace(/[/\\?%*:|"<>]/g, '_');

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.id}-${cleanCliente}-${cleanEquipo}-${cleanTipo}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      Swal.fire('Error', 'No se pudo generar el PDF', 'error');
    }
  };

  const handleOpenMaintenanceTypeModal = (equipo) => {
    setSelectedEquipoForMaintenance(equipo);
    setTypeDialogOpen(true);
  };

  const handleSelectMaintenanceType = (tipo) => {
    if (!selectedEquipoForMaintenance) return;
    const targetEquipo = selectedEquipoForMaintenance;
    setTypeDialogOpen(false);
    setSelectedEquipoForMaintenance(null);
    navigate(`/mantenimiento/formulario/${targetEquipo.id}?tipo=${encodeURIComponent(tipo)}`);
  };

  useEffect(() => {
    fetchClientes();
    fetchMySignatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const fetchMySignatures = async () => {
    try {
      const res = await axios.get('/getMySignatures');
      const sigs = res.data.data || [];
      setSavedSignatures(sigs);
      if (sigs.length === 0) setIsDrawingNewTech(true);
    } catch (e) {}
  };

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/getClientes/${company}?tipo=equipo`);
      const allClients = response.data.data || [];
      const uniqueClients = Array.from(new Map(allClients.map((item) => [item.id, item])).values());
      const formatted = uniqueClients.map((c) => {
        let principal = c.contacto_principal;
        if (typeof principal === 'string' && principal.trim()) {
          try {
            principal = JSON.parse(principal);
          } catch (e) {
            principal = null;
          }
        }
        return { ...c, principalContact: principal };
      });
      setClientes(formatted);
      localStorage.setItem(`dimei_cache_clientes_${company}`, JSON.stringify(formatted));
    } catch (error) {
      console.error('Error fetching clients for maintenance:', error);
      try {
        const cached = localStorage.getItem(`dimei_cache_clientes_${company}`);
        if (cached) setClientes(JSON.parse(cached));
      } catch (e) {}
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = async (cliente) => {
    if (expandedClientId === cliente.id) {
      setExpandedClientId(null);
      return;
    }

    setExpandedClientId(cliente.id);

    try {
      setLoadingEquipos((prev) => ({ ...prev, [cliente.id]: true }));
      const res = await axios.get(
        `/getProjects/${company}?tipo=equipo&customerId=${cliente.id}`
      );
      const eqs = res.data.data || [];
      setEquiposByClient((prev) => ({ ...prev, [cliente.id]: eqs }));
      localStorage.setItem(`dimei_cache_equipos_client_${cliente.id}`, JSON.stringify(eqs));
      // Pre-cache individual equipment for offline form access
      eqs.forEach((eq) => {
        localStorage.setItem(`dimei_cache_equipo_${eq.id}`, JSON.stringify(eq));
      });
    } catch (error) {
      console.error('Error fetching equipment:', error);
      try {
        const cachedEqs = localStorage.getItem(`dimei_cache_equipos_client_${cliente.id}`);
        if (cachedEqs) {
          setEquiposByClient((prev) => ({
            ...prev,
            [cliente.id]: JSON.parse(cachedEqs),
          }));
        }
      } catch (e) {}
    } finally {
      setLoadingEquipos((prev) => ({ ...prev, [cliente.id]: false }));
    }
  };

  // --- Flujo para recibir mantenimiento Pendiente de Entrega y entregarlo al cliente ---
  const handleOpenDeliveryModal = async (m, equipo, cliente) => {
    setDeliveringContext({ reportSummary: m, equipo, cliente });
    setDeliveryRecommendations(
      Array.isArray(m.recommendations) && m.recommendations.length > 0
        ? m.recommendations
        : []
    );
    setDeliveryObservations(m.general_observations || '');
    setCustomerName(m.customer_name || '');
    setDeliveryTechSignature(null);
    setDeliveryStep('delivery_tech');
    setIsDrawingNewTech(savedSignatures.length === 0);
    setDeliveryModalOpen(true);

    // Cargar detalle completo del reporte por si tiene recomendaciones u observaciones adicionales
    try {
      const res = await axios.get(`/getMaintenanceReport/${m.id}`);
      const full = res.data.data;
      if (full) {
        setDeliveringContext((prev) => ({ ...prev, fullReport: full }));
        if (Array.isArray(full.recommendations)) {
          setDeliveryRecommendations(full.recommendations);
        }
        if (full.general_observations) {
          setDeliveryObservations(full.general_observations);
        }
      }
    } catch (e) {}
  };

  const addDeliveryRecommendation = () => {
    setDeliveryRecommendations((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        description: '',
        quantity: '1',
        priority: 'Media',
        observation: '',
      },
    ]);
  };

  const updateDeliveryRecommendation = (idx, field, val) => {
    setDeliveryRecommendations((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });
  };

  const removeDeliveryRecommendation = (idx) => {
    setDeliveryRecommendations((prev) =>
      prev.filter((_, i) => i !== idx).map((item, i) => ({ ...item, id: i + 1 }))
    );
  };

  const handleUseSavedSigForDelivery = async (sig) => {
    const b64 = await fetchImageAsBase64(getImageUrl(sig.signature));
    if (b64) {
      setDeliveryTechSignature(b64);
      setDeliveryStep('customer');
    } else {
      Swal.fire('Error', 'No se pudo cargar la firma guardada.', 'error');
    }
  };

  const handleCompleteDelivery = async (custSigBase64) => {
    if (!customerName.trim()) {
      Swal.fire('Atención', 'Ingresa el nombre de quien recibe (cliente).', 'warning');
      return;
    }
    if (!deliveringContext?.reportSummary?.id) return;

    const reportId = deliveringContext.reportSummary.id;
    const { equipo, cliente } = deliveringContext;
    const cleanRecom = deliveryRecommendations.filter(
      (r) => r.description && r.description.trim() !== ''
    );

    try {
      setSubmittingDelivery(true);
      await axios.put(`/deliverMaintenanceReport/${reportId}`, {
        delivery_technician_id: userId,
        delivery_technician_signature: deliveryTechSignature,
        customer_name: customerName.trim(),
        customer_signature: custSigBase64,
        recommendations: cleanRecom,
        general_observations: deliveryObservations.trim(),
      });

      // Regenerar y descargar el PDF con las 3 firmas y los repuestos actualizados
      await handleDownloadReportPDF(reportId, equipo, cliente);

      setDeliveryModalOpen(false);
      await Swal.fire({
        icon: 'success',
        title: '¡Mantenimiento Entregado!',
        text: 'Se registraron los repuestos recomendados y las 3 firmas exitosamente.',
      });

      if (cliente?.id) {
        const res = await axios.get(
          `/getProjects/${company}?tipo=equipo&customerId=${cliente.id}`
        );
        setEquiposByClient((prev) => ({ ...prev, [cliente.id]: res.data.data || [] }));
      }
    } catch (error) {
      console.error('Error entregando mantenimiento:', error);
      Swal.fire('Error', 'No se pudo completar la entrega del mantenimiento.', 'error');
    } finally {
      setSubmittingDelivery(false);
    }
  };

  const filteredClientes = clientes.filter(
    (c) =>
      (c.nombre?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (c.nit?.toLowerCase() || '').includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: 2, pb: 8 }}>
      {/* Header Area */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', mb: 1 }}>
          Equipos y Mantenimientos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona un cliente para iniciar, continuar o hacer entrega de mantenimientos de equipos.
        </Typography>
      </Box>

      {/* Banner de Reportes Offline Pendientes de Sincronizar */}
      {offlineQueue.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 3, alignItems: 'center' }}
          action={
            <Button
              color="warning"
              variant="contained"
              size="small"
              disabled={syncingOffline || !navigator.onLine}
              startIcon={<ArrowPathIcon className="h-4 w-4" />}
              onClick={syncOfflineReports}
              sx={{ textTransform: 'none', fontWeight: '800', borderRadius: 2 }}
            >
              {syncingOffline ? 'Sincronizando...' : 'Sincronizar Ahora'}
            </Button>
          }
        >
          Tienes <b>{offlineQueue.length}</b> reporte(s) de mantenimiento guardados en modo{' '}
          <b>Offline (localStorage)</b> pendientes de subir al servidor.
        </Alert>
      )}

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Buscar cliente..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            border: 'none',
            '& fieldset': { border: 'none' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {filteredClientes.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No se encontraron clientes.</Typography>
            </Box>
          ) : (
            filteredClientes.map((cliente) => {
              const isExpanded = expandedClientId === cliente.id;
              const equipments = equiposByClient[cliente.id] || [];
              const isLoading = loadingEquipos[cliente.id];

              return (
                <Card
                  key={cliente.id}
                  sx={{
                    borderRadius: 4,
                    border: '1px solid #f1f5f9',
                    boxShadow: isExpanded
                      ? '0 20px 25px -5px rgb(0 0 0 / 0.1)'
                      : '0 4px 6px -1px rgb(0 0 0 / 0.05)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleExpand(cliente)}
                    >
                      <Avatar
                        sx={{
                          width: 52,
                          height: 52,
                          bgcolor: isExpanded ? '#1e293b' : '#eff6ff',
                          color: isExpanded ? '#fff' : '#3b82f6',
                          fontWeight: 'bold',
                          transition: 'all 0.3s',
                        }}
                      >
                        {cliente.nombre.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="700" noWrap sx={{ color: '#0f172a' }}>
                          {cliente.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          NIT: {cliente.nit || 'N/A'}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      </IconButton>
                    </Box>

                    {!isExpanded && (
                      <Box
                        sx={{
                          bgcolor: '#f8fafc',
                          px: 2,
                          py: 1,
                          borderTop: '1px solid #f1f5f9',
                          display: 'flex',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapPinIcon className="h-3.5 w-3.5 text-gray-400" />
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                            {cliente.direccion || 'Sin dirección'}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 2, bgcolor: '#fcfdfe' }}>
                        <Typography
                          variant="overline"
                          fontWeight="700"
                          color="primary"
                          sx={{ mb: 1.5, display: 'block' }}
                        >
                          Equipos Registrados
                        </Typography>

                        {isLoading ? (
                          <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <Stack spacing={1.5}>
                            {equipments.length === 0 ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic', py: 1 }}
                              >
                                No hay equipos vinculados a este cliente.
                              </Typography>
                            ) : (
                              equipments.map((equipo) => (
                                <Box
                                  key={equipo.id}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 3,
                                    bgcolor: '#fff',
                                    border: '1px solid #f1f5f9',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                      <Typography variant="body2" fontWeight="700">
                                        {equipo.nombre || `${equipo.elevatorTypeName} #${equipo.id}`}
                                      </Typography>
                                      {equipo.nombre && (
                                        <Typography variant="caption" color="text.secondary">
                                          {equipo.elevatorTypeName} #{equipo.id}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={equipo.state}
                                      size="small"
                                      variant="outlined"
                                      color={equipo.state === 'activo' ? 'success' : 'default'}
                                      sx={{
                                        height: 18,
                                        fontSize: '0.6rem',
                                        fontWeight: '800',
                                        textTransform: 'uppercase',
                                      }}
                                    />
                                  </Box>

                                  {/* Historial de Mantenimientos */}
                                  {(() => {
                                    const history =
                                      equipo.maintenanceHistory && equipo.maintenanceHistory.length > 0
                                        ? equipo.maintenanceHistory
                                        : equipo.lastMaintenance
                                        ? [equipo.lastMaintenance]
                                        : [];

                                    if (history.length === 0) {
                                      return (
                                        <Box
                                          sx={{
                                            mt: 0.5,
                                            mb: 1,
                                            p: 1,
                                            bgcolor: '#f8fafc',
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <WrenchScrewdriverIcon className="h-4 w-4 text-amber-500" />
                                          <Typography variant="caption" color="text.secondary">
                                            Sin mantenimientos registrados
                                          </Typography>
                                        </Box>
                                      );
                                    }

                                    return (
                                      <Box
                                        sx={{
                                          mt: 0.5,
                                          mb: 1,
                                          p: 1.2,
                                          bgcolor: '#f8fafc',
                                          borderRadius: 2,
                                          border: '1px solid #f1f5f9',
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          fontWeight="800"
                                          sx={{
                                            color: '#475569',
                                            display: 'block',
                                            mb: 0.8,
                                            textTransform: 'uppercase',
                                            fontSize: '0.65rem',
                                          }}
                                        >
                                          Mantenimientos Recientes / Pendientes:
                                        </Typography>
                                        <Stack spacing={0.8}>
                                          {history.map((m) => {
                                            const tipoNorm = (
                                              m.tipo ||
                                              m.maintenance_type ||
                                              ''
                                            ).toLowerCase();
                                            const isEmergencia = tipoNorm === 'emergencia';
                                            const isCorrectivo = tipoNorm === 'correctivo';
                                            const tipoColor = isEmergencia
                                              ? '#dc2626'
                                              : isCorrectivo
                                              ? '#d97706'
                                              : '#16a34a';
                                            const tipoBg = isEmergencia
                                              ? '#fee2e2'
                                              : isCorrectivo
                                              ? '#fef3c7'
                                              : '#dcfce7';
                                            const tipoLabel =
                                              m.tipo || m.maintenance_type || 'Preventivo';

                                            const isParcial = m.status === 'Parcial';
                                            const isPendienteEntrega =
                                              m.status === 'Pendiente de entrega';

                                            return (
                                              <Box
                                                key={m.id}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                  flexWrap: 'wrap',
                                                  gap: 1,
                                                  bgcolor: '#fff',
                                                  p: 1,
                                                  borderRadius: 1.5,
                                                  border: '1px solid #e2e8f0',
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.8,
                                                    flexWrap: 'wrap',
                                                  }}
                                                >
                                                  <Typography
                                                    variant="caption"
                                                    fontWeight="800"
                                                    sx={{
                                                      color: '#0f172a',
                                                      bgcolor: '#f1f5f9',
                                                      px: 0.8,
                                                      py: 0.2,
                                                      borderRadius: 1,
                                                    }}
                                                  >
                                                    #{m.id}
                                                  </Typography>
                                                  <Chip
                                                    label={tipoLabel}
                                                    size="small"
                                                    sx={{
                                                      height: 18,
                                                      fontSize: '0.62rem',
                                                      fontWeight: '800',
                                                      bgcolor: tipoBg,
                                                      color: tipoColor,
                                                    }}
                                                  />
                                                  <Chip
                                                    label={
                                                      isParcial
                                                        ? `Parcial${
                                                            m.continuedByTechnicianName
                                                              ? ` (Continúa: ${m.continuedByTechnicianName})`
                                                              : ''
                                                          }`
                                                        : m.status || 'Finalizado'
                                                    }
                                                    size="small"
                                                    color={
                                                      isParcial
                                                        ? 'warning'
                                                        : isPendienteEntrega
                                                        ? 'info'
                                                        : 'success'
                                                    }
                                                    variant="outlined"
                                                    sx={{
                                                      height: 18,
                                                      fontSize: '0.62rem',
                                                      fontWeight: '800',
                                                    }}
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ color: '#334155', fontWeight: '600' }}
                                                  >
                                                    {new Date(m.date).toLocaleDateString('es-CO')}
                                                  </Typography>
                                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                    Ejecutó: {m.technician || 'Técnico'}
                                                  </Typography>
                                                </Box>

                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                  {isParcial && (
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      color="warning"
                                                      startIcon={<PlayIcon className="h-3.5 w-3.5" />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(
                                                          `/mantenimiento/formulario/${equipo.id}?reportId=${m.id}&tipo=${encodeURIComponent(
                                                            tipoLabel
                                                          )}`
                                                        );
                                                      }}
                                                      sx={{
                                                        textTransform: 'none',
                                                        fontWeight: '800',
                                                        fontSize: '0.7rem',
                                                        borderRadius: 1.5,
                                                        py: 0.3,
                                                      }}
                                                    >
                                                      Continuar
                                                    </Button>
                                                  )}

                                                  {isPendienteEntrega && (
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      color="primary"
                                                      startIcon={<CheckCircleIcon className="h-3.5 w-3.5" />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenDeliveryModal(m, equipo, cliente);
                                                      }}
                                                      sx={{
                                                        textTransform: 'none',
                                                        fontWeight: '800',
                                                        fontSize: '0.7rem',
                                                        borderRadius: 1.5,
                                                        py: 0.3,
                                                        bgcolor: '#1e3a8a',
                                                      }}
                                                    >
                                                      Recibir y Entregar
                                                    </Button>
                                                  )}

                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDownloadReportPDF(m.id, equipo, cliente);
                                                    }}
                                                    sx={{
                                                      p: 0.6,
                                                      color: '#3b82f6',
                                                      bgcolor: 'rgba(59, 130, 246, 0.08)',
                                                      borderRadius: 1.5,
                                                    }}
                                                    title={`Descargar Reporte #${m.id}`}
                                                  >
                                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                                  </IconButton>
                                                </Stack>
                                              </Box>
                                            );
                                          })}
                                        </Stack>
                                      </Box>
                                    );
                                  })()}

                                  <Stack direction="row" spacing={2}>
                                    <Box>
                                      <Typography variant="caption" color="text.disabled" display="block">
                                        PARADAS
                                      </Typography>
                                      <Typography variant="caption" fontWeight="500">
                                        {equipo.stopNumber || '0'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.disabled" display="block">
                                        CAPACIDAD
                                      </Typography>
                                      <Typography variant="caption" fontWeight="500">
                                        {equipo.capacity || '0'} Kg
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Button
                                    fullWidth
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleOpenMaintenanceTypeModal(equipo)}
                                    startIcon={<CheckBadgeIcon className="h-4 w-4" />}
                                    sx={{
                                      mt: 0.5,
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: '600',
                                      bgcolor: '#3b82f6',
                                      '&:hover': { bgcolor: '#2563eb' },
                                    }}
                                  >
                                    Iniciar Nuevo Mantenimiento
                                  </Button>
                                </Box>
                              ))
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      )}

      {/* Dialog para seleccionar Tipo de Mantenimiento */}
      <Dialog
        open={typeDialogOpen}
        onClose={() => setTypeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight="800">
            Tipo de Mantenimiento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEquipoForMaintenance?.nombre ||
              `${selectedEquipoForMaintenance?.elevatorTypeName} #${
                selectedEquipoForMaintenance?.id || ''
              }`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper
              elevation={0}
              onClick={() => handleSelectMaintenanceType('Preventivo')}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '2px solid #e2e8f0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': { borderColor: '#10b981', bgcolor: '#f0fdf4' },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  bgcolor: '#dcfce7',
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShieldCheckIcon className="h-6 w-6" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="700">
                  Preventivo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Inspección y rutina periódica programada
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              onClick={() => handleSelectMaintenanceType('Correctivo')}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '2px solid #e2e8f0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': { borderColor: '#f59e0b', bgcolor: '#fffbeb' },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  bgcolor: '#fef3c7',
                  color: '#d97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WrenchScrewdriverIcon className="h-6 w-6" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="700">
                  Correctivo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reparación, ajuste o reemplazo por fallas
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              onClick={() => handleSelectMaintenanceType('Emergencia')}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '2px solid #e2e8f0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': { borderColor: '#ef4444', bgcolor: '#fef2f2' },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  bgcolor: '#fee2e2',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BoltIcon className="h-6 w-6" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="700">
                  Emergencia
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Atención inmediata por parada imprevista o urgencia
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth
            onClick={() => setTypeDialogOpen(false)}
            sx={{ textTransform: 'none', color: '#64748b', fontWeight: '700' }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG PARA RECIBIR Y HACER ENTREGA AL CLIENTE (Agregar Repuestos Recomendados + Firmas de Quien Entrega y Quien Recibe) */}
      <Dialog
        open={deliveryModalOpen}
        onClose={() => !submittingDelivery && setDeliveryModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: '800' }}>
          Entrega de Mantenimiento #{deliveringContext?.reportSummary?.id} al Cliente
          <Typography variant="caption" display="block" color="text.secondary">
            Ejecutado por: {deliveringContext?.reportSummary?.technician} | Entrega: {userName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Sección para agregar Repuestos o Trabajos Recomendados al momento de la entrega */}
          <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #cbd5e1', overflow: 'hidden' }}>
            <Box
              sx={{
                bgcolor: '#1e3a8a',
                px: 2,
                py: 1.2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#ffffff' }}>
                REPUESTOS / TRABAJOS RECOMENDADOS (PUEDES AGREGAR O EDITAR ANTES DE ENTREGAR)
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlusIcon className="h-4 w-4" />}
                onClick={addDeliveryRecommendation}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: '700',
                  borderRadius: 2,
                }}
              >
                Agregar Repuesto / Trabajo
              </Button>
            </Box>
            <CardContent sx={{ p: 2 }}>
              {deliveryRecommendations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No hay repuestos o trabajos recomendados registrados. Haz clic en &quot;Agregar Repuesto / Trabajo&quot; si deseas incluir alguno en la entrega.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {deliveryRecommendations.map((rec, rIdx) => (
                    <Paper
                      key={rIdx}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc' }}
                    >
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Descripción del repuesto o trabajo"
                            value={rec.description || ''}
                            onChange={(e) =>
                              updateDeliveryRecommendation(rIdx, 'description', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={4} sm={1.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Cant."
                            value={rec.quantity || '1'}
                            onChange={(e) =>
                              updateDeliveryRecommendation(rIdx, 'quantity', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={8} sm={2}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Prioridad"
                            value={rec.priority || 'Media'}
                            onChange={(e) =>
                              updateDeliveryRecommendation(rIdx, 'priority', e.target.value)
                            }
                            SelectProps={{ native: true }}
                          >
                            <option value="Alta">Alta</option>
                            <option value="Media">Media</option>
                            <option value="Baja">Baja</option>
                          </TextField>
                        </Grid>
                        <Grid item xs={10} sm={2.8}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Observación"
                            value={rec.observation || ''}
                            onChange={(e) =>
                              updateDeliveryRecommendation(rIdx, 'observation', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={2} sm={0.7} textAlign="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeDeliveryRecommendation(rIdx)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Observaciones generales / de entrega"
            value={deliveryObservations}
            onChange={(e) => setDeliveryObservations(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Stepper activeStep={deliveryStep === 'delivery_tech' ? 0 : 1} alternativeLabel sx={{ mb: 2 }}>
            <Step>
              <StepLabel>Firma Quien Entrega (Técnico)</StepLabel>
            </Step>
            <Step>
              <StepLabel>Firma Quien Recibe (Cliente)</StepLabel>
            </Step>
          </Stepper>

          {deliveryStep === 'delivery_tech' ? (
            <Box>
              {!isDrawingNewTech && savedSignatures.length > 0 ? (
                <Stack spacing={2} alignItems="center">
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3, bgcolor: '#f8fafc', textAlign: 'center' }}
                  >
                    <img
                      src={getImageUrl(savedSignatures[0].signature)}
                      alt="Firma Técnico"
                      style={{ maxHeight: 100, objectFit: 'contain' }}
                    />
                  </Paper>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleUseSavedSigForDelivery(savedSignatures[0])}
                    sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: '800' }}
                  >
                    Usar Mi Firma Guardada como Quien Entrega
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PencilIcon className="h-4 w-4" />}
                    onClick={() => setIsDrawingNewTech(true)}
                    sx={{ borderRadius: 2.5, textTransform: 'none' }}
                  >
                    Dibujar Nueva Firma
                  </Button>
                </Stack>
              ) : (
                <SignaturePad
                  title="Firma de Quien Entrega (Técnico / Empresa)"
                  onSave={(b64) => {
                    setDeliveryTechSignature(b64);
                    setDeliveryStep('customer');
                  }}
                  onCancel={
                    savedSignatures.length > 0 ? () => setIsDrawingNewTech(false) : null
                  }
                />
              )}
            </Box>
          ) : (
            <Box>
              <TextField
                fullWidth
                required
                label="Nombre completo de quien recibe (Cliente) *"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <SignaturePad
                title="Firma de Quien Recibe (Cliente)"
                onSave={handleCompleteDelivery}
              />
              <Button
                fullWidth
                variant="text"
                onClick={() => setDeliveryStep('delivery_tech')}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Volver a firma de quien entrega
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeliveryModalOpen(false)}
            disabled={submittingDelivery}
            sx={{ textTransform: 'none', color: '#64748b', fontWeight: '700' }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
